<template>
  <div>
    <!-- Loading Screen -->
    <div
      v-if="isLoading"
      class="fixed inset-0 flex items-center justify-center bg-[#fafbfc] z-[99999]"
    >
      <LottieLoader :src="lottieJson" />
    </div>

    <!-- Main Content -->
    <div
      v-else
      class="relative min-h-screen w-full overflow-hidden"
    >
      <!-- Two-tone Background -->
      <div class="absolute inset-0">
        <!-- Top Section - Background Image with Overlay (Banner Style) -->
        <div
          class="absolute top-0 left-0 right-0 h-[25%]"
          style="
            background-image: url('https://vamos-api-v2.sejadikopi.com/storage/images/login_bg.jpg');
            background-size: cover;
            background-position: center;
          "
        >
          <div class="absolute inset-0 bg-black/60" />
        </div>

        <!-- Wave SVG -->
        <svg
          class="absolute left-0 right-0 w-full"
          style="top: 30%; height: 60px"
          viewBox="0 0 1440 120"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
        >
          <path
            fill="#000000"
            fill-opacity="0.6"
            d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,0L1360,0C1280,0,1120,0,960,0C800,0,640,0,480,0C320,0,160,0,80,0L0,0Z"
          />
        </svg>

        <!-- Bottom Section - Very Light Gray -->
        <div class="absolute bottom-0 left-0 right-0 h-[75%] bg-[#fafbfc]" />
      </div>

      <!-- Header with Back Button -->
      <header class="absolute top-0 left-0 right-0 z-30">
        <div class="px-6 pt-4 pb-4 flex justify-between items-center">
          <!-- Back Button -->
          <button
            aria-label="Kembali"
            class="p-2.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all duration-200 active:scale-95 shadow-lg shadow-white/20 backdrop-blur-sm"
            @click="goBack"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-5 h-5 text-white drop-shadow-md"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <!-- Language Badge -->
          <div
            class="flex items-center gap-1.5 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/20"
          >
            <span class="text-white font-semibold text-xs">ID</span>
          </div>
        </div>
      </header>

      <!-- Main Content -->
      <main class="relative z-10 flex flex-col min-h-screen px-6 pt-16 pb-40">
        <!-- Welcome Text Section (Banner Area) -->
        <div class="flex flex-col items-center text-center py-8 mb-6">
          <h1 class="text-3xl font-bold text-white mb-2 drop-shadow-lg">
            Pilih Area Lokasi
          </h1>
          <p class="text-sm text-white/90 drop-shadow-md">
            Di mana Anda ingin duduk?
          </p>
        </div>

        <!-- Location Buttons Section (White Area) -->
        <div class="w-full max-w-md mx-auto mb-8 flex-1">
          <!-- Buttons Container with Background -->
          <div
            class="bg-gradient-to-br from-[#f5f7fa] to-[#e8ecf1] rounded-[32px] p-6 shadow-sm border border-white/60"
          >
            <!-- Card Title -->
            <div class="mb-6">
              <h2 class="text-xl font-bold text-[#2c3e50] mb-1">
                Pilih Lokasi Duduk
              </h2>
              <p class="text-sm text-[#7f8c8d]">
                Pilih area yang paling sesuai dengan kenyamanan Anda
              </p>
            </div>

            <div class="space-y-3">
              <!-- Billiard Button -->
              <button
                class="group w-full bg-white backdrop-blur-sm rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 active:scale-[0.98] overflow-hidden border border-gray-100 p-4"
                @click="selectArea('Billiard')"
              >
                <div class="flex items-center gap-3">
                  <div
                    class="w-12 h-12 bg-gradient-to-br from-[#e74c3c] to-[#c0392b] rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="w-6 h-6 text-white"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                      />
                      <circle
                        cx="12"
                        cy="12"
                        r="3"
                        fill="#fff7e8"
                      />
                      <path
                        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"
                        opacity="0.3"
                      />
                    </svg>
                  </div>
                  <div class="flex-1 text-left">
                    <div class="text-base font-bold text-[#2c3e50]">
                      Biliar
                    </div>
                    <div class="text-xs text-[#7f8c8d]">
                      Billiard
                    </div>
                  </div>
                </div>
              </button>

              <!-- Dalam Ruangan Button -->
              <button
                class="group w-full bg-white backdrop-blur-sm rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 active:scale-[0.98] overflow-hidden border border-gray-100 p-4"
                @click="selectArea('Indoor')"
              >
                <div class="flex items-center gap-3">
                  <div
                    class="w-12 h-12 bg-gradient-to-br from-[#9CA3AF] to-[#7a8088] rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="w-6 h-6 text-white"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
                    </svg>
                  </div>
                  <div class="flex-1 text-left">
                    <div class="text-base font-bold text-[#2c3e50]">
                      Dalam Ruangan
                    </div>
                    <div class="text-xs text-[#7f8c8d]">
                      Indoor
                    </div>
                  </div>
                </div>
              </button>

              <!-- Luar Ruangan Button -->
              <button
                class="group w-full bg-white backdrop-blur-sm rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 active:scale-[0.98] overflow-hidden border border-gray-100 p-4"
                @click="selectArea('Outdoor')"
              >
                <div class="flex items-center gap-3">
                  <div
                    class="w-12 h-12 bg-gradient-to-br from-[#5dade2] to-[#3498db] rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="w-6 h-6 text-white"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path
                        d="M17.66 8L12 2.35 6.34 8C4.78 9.56 4 11.64 4 13.64s.78 4.11 2.34 5.67 3.61 2.35 5.66 2.35 4.1-.79 5.66-2.35S20 15.64 20 13.64 19.22 9.56 17.66 8zM6 14c.01-2 .62-3.27 1.76-4.4L12 5.27l4.24 4.38C17.38 10.77 17.99 12 18 14H6z"
                      />
                    </svg>
                  </div>
                  <div class="flex-1 text-left">
                    <div class="text-base font-bold text-[#2c3e50]">
                      Luar Ruangan
                    </div>
                    <div class="text-xs text-[#7f8c8d]">
                      Outdoor
                    </div>
                  </div>
                </div>
              </button>

              <!-- Barber Button -->
              <button
                class="group w-full bg-white backdrop-blur-sm rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 active:scale-[0.98] overflow-hidden border border-gray-100 p-4"
                @click="selectArea('Barber')"
              >
                <div class="flex items-center gap-3">
                  <div
                    class="w-12 h-12 bg-gradient-to-br from-[#9b59b6] to-[#8e44ad] rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="w-6 h-6 text-white"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M20.24 6.17l-2.41 2.41-3.54-3.54 2.41-2.41c.78-.78 2.05-.78 2.83 0l.71.71c.78.78.78 2.05 0 2.83zM7.05 12.36l-4.24 4.24c-.39.39-.39 1.02 0 1.41.39.39 1.02.39 1.41 0l4.24-4.24 1.41 1.41L6.63 19.41c-.78.78-2.05.78-2.83 0l-.71-.71c-.78-.78-.78-2.05 0-2.83l4.24-4.24 1.72 1.73zM14.06 9.3l3.54 3.54-1.41 1.41-3.54-3.54 1.41-1.41z" />
                    </svg>
                  </div>
                  <div class="flex-1 text-left">
                    <div class="text-base font-bold text-[#2c3e50]">
                      Tempat cukur
                    </div>
                    <div class="text-xs text-[#7f8c8d]">
                      Barber
                    </div>
                  </div>
                </div>
              </button>
            </div>

            <!-- Info Card Section -->
            <div
              class="mt-6 bg-white/50 backdrop-blur-sm rounded-2xl p-4 border border-white/80"
            >
              <p class="text-xs text-center text-[#7f8c8d] leading-relaxed">
                Pilih area yang sesuai dengan kenyamanan Anda
              </p>
            </div>
          </div>
        </div>
      </main>

      <!-- Fixed Footer at Bottom -->
      <footer
        class="fixed bottom-0 left-0 right-0 z-40 bg-gradient-to-t from-white via-white/95 to-transparent backdrop-blur-md border-t border-[#ecf0f1] shadow-2xl shadow-black/5"
      >
        <div class="w-full px-6 py-4">
          <div class="flex flex-col items-center gap-2.5">
            <!-- Professional Copyright Text -->
            <div class="text-center">
              <p class="text-xs font-medium text-[#34495e]">
                © 2025 Vamos Pool Cafe. All rights reserved.
              </p>
              <p class="text-xs text-[#7f8c8d] mt-0.5">
                Designed & Developed by
                <span class="font-semibold text-[#2c3e50]">Productivty Era</span>
              </p>
            </div>

            <!-- Logo Divider -->
            <div class="flex items-center gap-3">
              <img
                src="https://vamos-api-v2.sejadikopi.com/storage/Logo/vamos_logo.png"
                alt="Vamos Logo"
                class="h-5 w-auto opacity-60"
                draggable="false"
              >
              <div class="w-1 h-4 bg-[#bdc3c7]" />
              <img
                src="https://vamos-api-v2.sejadikopi.com/storage/Logo/logo_rpl.png"
                alt="RPL Logo"
                class="h-5 w-auto opacity-60"
                draggable="false"
              >
            </div>
          </div>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import LottieLoader from "@/components/LottieLoader.vue";
import lottieJson from "../assets/loader.json";

export default {
  name: "SelectLocationArea",
  components: { LottieLoader },
  data() {
    return {
      isLoading: true,
      lottieJson,
    };
  },
  mounted() {
    // Loading simulation
    setTimeout(() => {
      this.isLoading = false;
    }, 1200);
  },
  methods: {
    selectArea(area) {
      // Simpan area yang dipilih ke localStorage
      localStorage.setItem("locationArea", area);

      // Redirect ke halaman SelectTable
      this.$router.push({ name: "SelectTable" });
    },

    goBack() {
      // Kembali ke halaman sebelumnya
      this.$router.go(-1);
    },
  },
};
</script>

<style scoped>
/* Fade In Animation */
.animate-fade-in {
  animation: fadeIn 0.7s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Backdrop Blur */
.backdrop-blur-sm {
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.backdrop-blur-md {
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

/* Button hover effects */
button {
  transition: all 0.3s ease;
}

button:hover:not(:disabled) {
  transform: translateY(-1px);
}

button:active:not(:disabled) {
  transform: translateY(0);
}
</style>
